// Phase 4.3: Widget Renderer with Analytics Widgets
import { fmtPercent, trendArrow } from "./format";
import { ProjectHealthWidget } from "./project-health";
import { ResourceUtilizationWidget } from "./resource-utilization";
import { ConflictTrendsWidget } from "./conflict-trends";
import type { DashboardWidget } from "../types";
import type { WidgetFilters } from "./types";
import {
  hasLiveWidgetRenderer,
  UNSUPPORTED_WIDGET_COPY,
} from "../widget-allowlist";

type LegacyWidget =
  | { id: string; type: 'kpi'; label: string; value: string; trend?: string; config?: any }
  | { id: string; type: 'note'; text: string }
  | { id: string; type: 'table'; columns: string[]; rows: string[][] }
  | { id: string; type: 'chart'; chartType: 'line' | 'bar'; series: number[] };

type Props = {
  widget: LegacyWidget | DashboardWidget;
  data?: any;
  filters?: WidgetFilters;
  isShareMode?: boolean; // If true, show "Sign in to load live data" for analytics widgets
};

function widgetKeyOf(widget: LegacyWidget | DashboardWidget): string {
  if ('widgetKey' in widget && typeof widget.widgetKey === 'string') {
    return widget.widgetKey;
  }
  if ('type' in widget && typeof widget.type === 'string') {
    return widget.type;
  }
  return '__invalid__';
}

function UnsupportedWidgetPlaceholder({ id }: { id: string }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500"
      data-testid={`widget-unsupported-${id}`}
    >
      {UNSUPPORTED_WIDGET_COPY}
    </div>
  );
}

export function WidgetRenderer({ widget, data, filters, isShareMode = false }: Props) {
  const key = widgetKeyOf(widget);
  const isDashboardWidget =
    ('widgetKey' in widget && typeof widget.widgetKey === 'string' && 'layout' in widget) ||
    ('type' in widget && typeof widget.type === 'string' && 'layout' in widget);
  const dashboardWidget = isDashboardWidget ? (widget as DashboardWidget) : null;

  // Legacy local `type: 'kpi'` (no layout) — false-zero display path.
  // DashboardWidget `widgetKey: 'kpi'` is dropped (Agent 2): unsupported placeholder.
  if (!dashboardWidget && key === 'kpi') {
    const label =
      (widget as { config?: { label?: string }; label?: string; title?: string }).config?.label ??
      (widget as { label?: string }).label ??
      (widget as { title?: string }).title ??
      'KPI';
    const trend = data?.trend;
    const status = data?.status as string | undefined;
    const error = data?.error === true;

    if (status === 'restricted') {
      return (
        <div data-testid={`widget-${widget.id}`} className="p-4 border rounded">
          <div className="text-sm text-neutral-600">{label}</div>
          <div className="text-sm text-neutral-400 mt-1">Restricted</div>
        </div>
      );
    }
    if (status === 'not_enough_data') {
      return (
        <div data-testid={`widget-${widget.id}`} className="p-4 border rounded">
          <div className="text-sm text-neutral-600">{label}</div>
          <div className="text-sm text-neutral-400 mt-1">Not enough data</div>
        </div>
      );
    }
    if (error) {
      return (
        <div data-testid={`widget-${widget.id}`} className="p-4 border rounded">
          <div className="text-sm text-neutral-400">{label} — unavailable</div>
        </div>
      );
    }
    if (typeof data?.ratio === 'number' && Number.isFinite(data.ratio)) {
      return (
        <div data-testid={`widget-${widget.id}`} className="p-4 border rounded flex flex-col gap-1">
          <div className="text-sm text-neutral-600">{label}</div>
          <div className="text-3xl font-semibold">{fmtPercent(data.ratio)}</div>
        </div>
      );
    }
    if (typeof data?.value === 'number' && Number.isFinite(data.value)) {
      return (
        <div data-testid={`widget-${widget.id}`} className="p-4 border rounded flex flex-col gap-1">
          <div className="text-sm text-neutral-600">{label}</div>
          <div className="text-3xl font-semibold">{data.value.toLocaleString()}</div>
          {trend && <div className="text-xs text-neutral-500">{trendArrow(trend)}</div>}
        </div>
      );
    }
    return (
      <div data-testid={`widget-${widget.id}`} className="p-4 border rounded">
        <div className="text-sm text-neutral-600">{label}</div>
        <div className="text-sm text-neutral-400 mt-1">Not enough data</div>
      </div>
    );
  }

  if (
    dashboardWidget?.unsupported === true ||
    !hasLiveWidgetRenderer(key)
  ) {
    return <UnsupportedWidgetPlaceholder id={widget.id} />;
  }

  if (dashboardWidget) {
    const widgetFilters: WidgetFilters = filters || {
      startDate: undefined,
      endDate: undefined,
    };

    // In share mode, show sign-in prompt for analytics widgets
    if (isShareMode) {
      return (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
          <p className="text-sm font-medium mb-2">Sign in required</p>
          <p className="text-xs">Please sign in to load live data for this widget.</p>
        </div>
      );
    }

    switch (key) {
      case 'project_health':
        return <ProjectHealthWidget widget={dashboardWidget} filters={widgetFilters} workspaceId={null} />;

      case 'resource_utilization':
        return <ResourceUtilizationWidget widget={dashboardWidget} filters={widgetFilters} workspaceId={null} />;

      case 'conflict_trends':
        return <ConflictTrendsWidget widget={dashboardWidget} filters={widgetFilters} workspaceId={null} />;

      default:
        return <UnsupportedWidgetPlaceholder id={widget.id} />;
    }
  }

  // Remaining legacy widget types (note / table / chart)
  const legacyWidget = widget as LegacyWidget;

  if (legacyWidget.type === 'note') {
    const d = data ?? { text: legacyWidget.text ?? 'Note' };
    return (
      <div className="rounded-lg border p-3 shadow-sm" data-testid={`widget-${widget.id}`}>
        <div className="whitespace-pre-wrap text-sm">{d.text}</div>
      </div>
    );
  }

  if (legacyWidget.type === 'table') {
    const d = data ?? { columns: legacyWidget.columns ?? [], rows: legacyWidget.rows ?? [] };
    return (
      <div className="rounded-lg border p-3 shadow-sm" data-testid={`widget-${widget.id}`}>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr>{d.columns.map((c: string) => <th key={c} className="px-2 py-1 text-left">{c}</th>)}</tr>
            </thead>
            <tbody>
              {d.rows.map((r: any, i: number) => (
                <tr key={i} className="even:bg-neutral-50 hover:bg-neutral-100">
                  {d.columns.map((c: string, j: number) => <td key={`${i}-${j}`} className="px-2 py-1 truncate">{r[c]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (legacyWidget.type === 'chart') {
    const d = data ?? { chartType: legacyWidget.chartType ?? 'line', series: legacyWidget.series ?? [] };
    return (
      <div className="rounded-lg border p-3 shadow-sm" data-testid={`widget-${widget.id}`}>
        <div className="text-xs text-neutral-500 mb-1">Chart ({d.chartType})</div>
        <div className="text-sm">{JSON.stringify(d.series)}</div>
      </div>
    );
  }

  return <UnsupportedWidgetPlaceholder id={widget.id} />;
}
